import { useTranslation } from "react-i18next";
import { useAo3Site } from "@/hooks/ao3SiteContext";
import { ao3ScrollToSection } from "@/hooks/ao3ScrollToSection";
import { ao3Assets } from "@/mocks/ao3Home";

export default function Ao3Hero() {
  const { t } = useTranslation();
  const { openDownload } = useAo3Site();

  const quickStats = [t("hero.stat1"), t("hero.stat2"), t("hero.stat3")];

  return (
    <section id="ao3-top" className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={ao3Assets.heroBackground}
          alt="Abstract crimson ink artwork on warm paper"
          title="AO3 Reader — Archive of Our Own"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/75" />
        <div className="absolute inset-0 bg-primary-950/25 dark:bg-primary-100/30" />
      </div>

      <div className="relative mx-auto flex w-full min-h-[640px] md:min-h-[720px] flex-col items-center justify-center px-4 md:px-6 pt-28 pb-16 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/10 px-4 py-1.5 text-xs md:text-sm font-medium tracking-[0.14em] text-background-50 dark:border-foreground-950/30 dark:bg-foreground-950/10 dark:text-foreground-950">
          <i className="ri-book-marked-line text-sm leading-none text-accent-300 dark:text-accent-500" />
          {t("hero.badge")}
        </span>

        <h1 className="mt-6 flex flex-col items-center">
          <span className="font-heading text-5xl font-semibold leading-none tracking-tight text-background-50 dark:text-foreground-950 md:text-7xl lg:text-8xl">
            AO3
          </span>
          <span className="mt-4 max-w-4xl font-heading text-xl leading-snug text-background-50/90 dark:text-foreground-950/90 md:text-3xl lg:text-4xl">
            {t("hero.title")}
          </span>
        </h1>

        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-50/80 dark:text-foreground-950/85 md:text-base">{t("hero.desc")}</p>

        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={openDownload}
            className="inline-flex items-center gap-2 cursor-pointer rounded-md bg-primary-500 px-6 py-3 text-sm md:text-base font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            <i className="ri-download-2-line text-lg leading-none" />
            {t("hero.ctaPrimary")}
          </button>
          <button
            type="button"
            onClick={() => ao3ScrollToSection("ao3-features")}
            className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-background-50/40 px-6 py-3 text-sm md:text-base font-medium whitespace-nowrap text-background-50 transition-colors hover:bg-background-50/15 dark:border-foreground-950/40 dark:text-foreground-950 dark:hover:bg-foreground-950/15"
          >
            <i className="ri-compass-3-line text-lg leading-none" />
            {t("hero.ctaSecondary")}
          </button>
        </div>

        <p className="mt-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-background-50/70 dark:text-foreground-950/75 md:text-sm">
          {quickStats.map((stat, index) => (
            <span key={stat} className="flex items-center gap-2">
              {index > 0 && <span className="text-accent-300 dark:text-accent-500">·</span>}
              {stat}
            </span>
          ))}
        </p>

        <p className="mt-2 text-xs text-background-50/60 dark:text-foreground-950/65">{t("hero.note")}</p>
      </div>

      <img
        src={ao3Assets.mascotReading}
        alt="ao3-1"
        className="pointer-events-none absolute bottom-6 left-6 hidden w-28 xl:block animate-ao3-float"
      />
      <img
        src={ao3Assets.mascotWalking}
        alt="ao3-2"
        className="pointer-events-none absolute bottom-10 right-6 hidden w-24 xl:block animate-ao3-float"
      />
    </section>
  );
}